import { useState, useCallback } from 'react';
import { parseISO } from 'date-fns';

interface IUseSelectDatesProps {
  initialDate?: string; // YYYY-MM-DD format from location.state (Calendar page entry)
  initialDates?: Date[]; // Pre-selected dates from form context (when navigating back)
}

interface IUseSelectDatesReturn {
  selectedDates: Date[];
  handleCalendarSelect: (dates: string[]) => void;
  handleRemoveDate: (date: Date) => void;
  selectedCount: number;
  canContinue: boolean;
  tipVisible: boolean;
  nextButtonText: string;
}

export const useSelectDates = ({
  initialDate,
  initialDates,
}: IUseSelectDatesProps = {}): IUseSelectDatesReturn => {
  // Initialize with pre-selected dates from form context, or single date from location.state
  const [selectedDates, setSelectedDates] = useState<Date[]>(() => {
    if (initialDates && initialDates.length > 0) return initialDates;
    if (initialDate) {
      const parsed = parseISO(initialDate);
      if (!isNaN(parsed.getTime())) return [parsed];
    }
    return [];
  });

  // CalendarSelection.onSelect gives us an array of YYYY-MM-DD strings
  // This is the FULL list of selected dates (not a diff), so we just replace
  const handleCalendarSelect = useCallback((dates: string[]) => {
    setSelectedDates(dates.map((d) => parseISO(d)));
  }, []);

  // Remove a specific date
  const handleRemoveDate = useCallback((dateToRemove: Date) => {
    setSelectedDates((prev) =>
      prev.filter((d) => d.getTime() !== dateToRemove.getTime()),
    );
  }, []);

  const selectedCount = selectedDates.length;
  const canContinue = selectedCount > 0;
  const tipVisible = selectedCount < 2;
  const nextButtonText = `Next (${selectedCount} ${selectedCount === 1 ? 'Day' : 'Days'} Selected)`;

  return {
    selectedDates,
    handleCalendarSelect,
    handleRemoveDate,
    selectedCount,
    canContinue,
    tipVisible,
    nextButtonText,
  };
};
